/**
 * Alarm Service
 * Alarm işlemleri için service (mevcut projende AlarmDetail, AlarmHistory var)
 */

import { apiClient } from '../client/apiClient';
import { ENDPOINTS, createEndpoint } from '../config/endpoints';
import { AlarmSeverity, AlarmStatus } from '../types/requests/common.types';
import {
  // Types
  Alarm,
  AlarmStatistics,
  AlarmType,
  
  // Request/Response
  PaginatedResponse,
  
  // Query Params
  BaseQueryParams,
  DateRangeFilter,
  ID,
  
  // Export
  ExportFormat
} from '../types/api.types';

/**
 * Alarm query parameters
 */
export interface AlarmQueryParams extends BaseQueryParams, DateRangeFilter {
  severity?: AlarmSeverity | AlarmSeverity[];
  status?: AlarmStatus | AlarmStatus[];
  unitId?: ID | ID[];
  wagonId?: ID | ID[];
  bogieId?: ID | ID[];
  typeId?: ID | ID[];
  acknowledged?: boolean;
  resolved?: boolean;
  criticalOnly?: boolean;
  includeMetadata?: boolean;
  excludeFalseAlarms?: boolean;
}

/**
 * Acknowledge request
 */
export interface AcknowledgeRequest {
  alarmIds: ID[];
  acknowledgedBy: string;
  comment?: string;
}

/**
 * Resolve request
 */
export interface ResolveRequest {
  alarmIds: ID[];
  resolvedBy: string;
  resolution?: string;
  comment?: string;
}

/**
 * Alarm filter options
 */
export interface AlarmFilterOptions {
  severities: AlarmSeverity[];
  statuses: AlarmStatus[];
  units: Array<{ id: ID; name: string }>;
  types: AlarmType[];
  dateRange: {
    start: Date;
    end: Date;
  };
}

/**
 * Alarm Service Interface
 */
export interface IAlarmService {
  // CRUD Operations
  getAlarms(params?: AlarmQueryParams): Promise<PaginatedResponse<Alarm>>;
  getAlarm(id: ID): Promise<Alarm>;
  getAlarmByAlarmId(alarmId: string): Promise<Alarm>;
  createAlarm(alarm: Partial<Alarm>): Promise<Alarm>;
  updateAlarm(id: ID, alarm: Partial<Alarm>): Promise<Alarm>;
  deleteAlarm(id: ID): Promise<void>;
  
  // Bulk Operations
  acknowledgeAlarms(request: AcknowledgeRequest): Promise<void>;
  resolveAlarms(request: ResolveRequest): Promise<void>;
  bulkDeleteAlarms(alarmIds: ID[]): Promise<void>;
  
  // Special Queries
  getActiveAlarms(params?: AlarmQueryParams): Promise<PaginatedResponse<Alarm>>;
  getCriticalAlarms(params?: AlarmQueryParams): Promise<PaginatedResponse<Alarm>>;
  getUnacknowledgedAlarms(params?: AlarmQueryParams): Promise<PaginatedResponse<Alarm>>;
  getAlarmsByUnit(unitId: ID, params?: AlarmQueryParams): Promise<PaginatedResponse<Alarm>>;
  getAlarmsByWagon(wagonId: ID, params?: AlarmQueryParams): Promise<PaginatedResponse<Alarm>>;
  getRecentAlarms(limit?: number): Promise<Alarm[]>;
  
  // Statistics & Analytics
  getAlarmStatistics(params?: AlarmQueryParams): Promise<AlarmStatistics>;
  getAlarmTrends(dateRange: DateRangeFilter): Promise<any>;
  getAlarmDistribution(): Promise<any>;
  getTopAlarmingUnits(limit?: number): Promise<Array<{ unitId: ID; unitName: string; count: number }>>;
  getAlarmCountBySeverity(): Promise<Record<AlarmSeverity, number>>;
  getAlarmCountByStatus(): Promise<Record<AlarmStatus, number>>;
  
  // Alarm History
  getAlarmHistory(alarmId: ID): Promise<Alarm[]>;
  getAlarmTimeline(alarmId: ID): Promise<any>;
  getRelatedAlarms(alarmId: ID): Promise<Alarm[]>;
  
  // Export Operations
  exportAlarms(format: ExportFormat, params?: AlarmQueryParams): Promise<Blob>;
  exportAlarmReport(alarmId: ID, format: ExportFormat): Promise<Blob>;
  
  // Filter & Configuration
  getAlarmFilters(): Promise<AlarmFilterOptions>;
  getAlarmTypes(): Promise<AlarmType[]>;
  getAlarmConfig(): Promise<any>;
  
  // Real-time/WebSocket
  subscribeToAlarms(callback: (alarm: Alarm) => void): () => void;
  unsubscribeFromAlarms(): void;
  getAlarmStream(params?: AlarmQueryParams): EventSource;
}

/**
 * Alarm Service Implementation
 */
export class AlarmService implements IAlarmService {
  private alarmStream: EventSource | null = null;
  private subscribers: Array<(alarm: Alarm) => void> = [];

  // ==================== CRUD OPERATIONS ====================

  async getAlarms(params?: AlarmQueryParams): Promise<PaginatedResponse<Alarm>> {
    try {
      const endpoint = ENDPOINTS.ALARMS.LIST;
      const cacheKey = this.generateCacheKey('alarms', params);
      
      return await apiClient.getList<Alarm>(endpoint, params, {
        cacheKey,
        cacheTTL: 30 * 1000, // 30 saniye
        params: this.normalizeQueryParams(params)
      });
    } catch (error) {
      console.error('Get alarms error:', error);
      throw error;
    }
  }

  async getAlarm(id: ID): Promise<Alarm> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.ALARMS.DETAIL, id);
      const cacheKey = `alarm_${id}`;
      
      const response = await apiClient.get<Alarm>(endpoint, id, {
        cacheKey,
        cacheTTL: 60 * 1000 // 1 dakika
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get alarm ${id} error:`, error);
      throw error;
    }
  }

  async getAlarmByAlarmId(alarmId: string): Promise<Alarm> {
    try {
      const endpoint = `${ENDPOINTS.ALARMS.BASE}/by-alarm-id/${alarmId}`;
      const cacheKey = `alarm_by_id_${alarmId}`;
      
      const response = await apiClient.customGet<Alarm>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get alarm by alarmId ${alarmId} error:`, error);
      throw error;
    }
  }

  async createAlarm(alarm: Partial<Alarm>): Promise<Alarm> {
    try {
      const endpoint = ENDPOINTS.ALARMS.BASE;
      const response = await apiClient.customPost<Alarm>(endpoint, alarm);
      
      // Cache'i temizle
      this.clearAlarmsCache();
      
      return response.data;
    } catch (error) {
      console.error('Create alarm error:', error);
      throw error;
    }
  }

  async updateAlarm(id: ID, alarm: Partial<Alarm>): Promise<Alarm> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.ALARMS.DETAIL, id);
      const response = await apiClient.customPut<Alarm>(endpoint, alarm);
      
      // Cache'i temizle
      this.clearAlarmCache(id);
      this.clearAlarmsCache();
      
      return response.data;
    } catch (error) {
      console.error(`Update alarm ${id} error:`, error);
      throw error;
    }
  }

  async deleteAlarm(id: ID): Promise<void> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.ALARMS.DETAIL, id);
      await apiClient.delete(endpoint, id);
      
      // Cache'i temizle
      this.clearAlarmCache(id);
      this.clearAlarmsCache();
    } catch (error) {
      console.error(`Delete alarm ${id} error:`, error);
      throw error;
    }
  }

  // ==================== BULK OPERATIONS ====================

  async acknowledgeAlarms(request: AcknowledgeRequest): Promise<void> {
    try {
      const endpoint = `${ENDPOINTS.ALARMS.BASE}/acknowledge`;
      await apiClient.customPost<void>(endpoint, request);
      
      // Cache'i temizle
      this.clearAlarmsCache();
      for (const id of request.alarmIds) {
        this.clearAlarmCache(id);
      }
    } catch (error) {
      console.error('Acknowledge alarms error:', error);
      throw error;
    }
  }

  async resolveAlarms(request: ResolveRequest): Promise<void> {
    try {
      const endpoint = `${ENDPOINTS.ALARMS.BASE}/resolve`;
      await apiClient.customPost<void>(endpoint, request);
      
      // Cache'i temizle
      this.clearAlarmsCache();
      for (const id of request.alarmIds) {
        this.clearAlarmCache(id);
      }
    } catch (error) {
      console.error('Resolve alarms error:', error);
      throw error;
    }
  }

  async bulkDeleteAlarms(alarmIds: ID[]): Promise<void> {
    try {
      const endpoint = ENDPOINTS.ALARMS.BULK;
      await apiClient.customDelete<void>(endpoint, {
        data: { alarmIds }
      });
      
      // Cache'i temizle
      this.clearAlarmsCache();
      for  (const id of alarmIds) {
        this.clearAlarmCache(id);
      }
    } catch (error) {
      console.error('Bulk delete alarms error:', error);
      throw error;
    }
  }

  // ==================== SPECIAL QUERIES ====================

  async getActiveAlarms(params?: AlarmQueryParams): Promise<PaginatedResponse<Alarm>> {
    const activeParams: AlarmQueryParams = {
      ...params,
      status: 'active',
      resolved: false
    };
    
    return this.getAlarms(activeParams);
  }

  async getCriticalAlarms(params?: AlarmQueryParams): Promise<PaginatedResponse<Alarm>> {
    const criticalParams: AlarmQueryParams = {
      ...params,
      severity: ['critical', 'high'],
      criticalOnly: true
    };
    
    return this.getAlarms(criticalParams);
  }

  async getUnacknowledgedAlarms(params?: AlarmQueryParams): Promise<PaginatedResponse<Alarm>> {
    const unacknowledgedParams: AlarmQueryParams = {
      ...params,
      acknowledged: false
    };
    
    return this.getAlarms(unacknowledgedParams);
  }

  async getAlarmsByUnit(unitId: ID, params?: AlarmQueryParams): Promise<PaginatedResponse<Alarm>> {
    const unitParams: AlarmQueryParams = {
      ...params,
      unitId
    };
    
    return this.getAlarms(unitParams);
  }

  async getAlarmsByWagon(wagonId: ID, params?: AlarmQueryParams): Promise<PaginatedResponse<Alarm>> {
    const wagonParams: AlarmQueryParams = {
      ...params,
      wagonId
    };
    
    return this.getAlarms(wagonParams);
  }

  async getRecentAlarms(limit: number = 10): Promise<Alarm[]> {
    try {
      const endpoint = `${ENDPOINTS.ALARMS.BASE}/recent`;
      const cacheKey = `recent_alarms_${limit}`;
      
      const response = await apiClient.customGet<Alarm[]>(endpoint, {
        cacheKey,
        cacheTTL: 15 * 1000, // 15 saniye
        params: { limit }
      });
      
      return response.data || [];
    } catch (error) {
      console.error('Get recent alarms error:', error);
      throw error;
    }
  }

  // ==================== STATISTICS & ANALYTICS ====================

  async getAlarmStatistics(params?: AlarmQueryParams): Promise<AlarmStatistics> {
    try {
      const endpoint = ENDPOINTS.ALARMS.STATISTICS;
      const cacheKey = this.generateCacheKey('alarm_stats', params);
      
      const response = await apiClient.customGet<AlarmStatistics>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000, // 1 dakika
        params: this.normalizeQueryParams(params)
      });
      
      return response.data;
    } catch (error) {
      console.error('Get alarm statistics error:', error);
      throw error;
    }
  }

  async getAlarmTrends(dateRange: DateRangeFilter): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.ALARMS.BASE}/trends`;
      const cacheKey = `alarm_trends_${JSON.stringify(dateRange)}`;
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000, // 5 dakika
        params: dateRange
      });
      
      return response.data;
    } catch (error) {
      console.error('Get alarm trends error:', error);
      throw error;
    }
  }

  async getAlarmDistribution(): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.ALARMS.BASE}/distribution`;
      const cacheKey = 'alarm_distribution';
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheKey,
        cacheTTL: 5 * 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error('Get alarm distribution error:', error);
      throw error;
    }
  }

  async getTopAlarmingUnits(limit: number = 5): Promise<Array<{ unitId: ID; unitName: string; count: number }>> {
    try {
      const endpoint = `${ENDPOINTS.ALARMS.BASE}/top-units`;
      const cacheKey = `top_alarming_units_${limit}`;
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheKey,
        cacheTTL: 2 * 60 * 1000, // 2 dakika
        params: { limit }
      });
      
      return response.data || [];
    } catch (error) {
      console.error('Get top alarming units error:', error);
      throw error;
    }
  }

  async getAlarmCountBySeverity(): Promise<Record<AlarmSeverity, number>> {
    try {
      const endpoint = `${ENDPOINTS.ALARMS.BASE}/count-by-severity`;
      const cacheKey = 'alarm_count_by_severity';
      
      const response = await apiClient.customGet<Record<AlarmSeverity, number>>(endpoint, {
        cacheKey,
        cacheTTL: 30 * 1000 // 30 saniye
      });
      
      return response.data || {};
    } catch (error) {
      console.error('Get alarm count by severity error:', error);
      throw error;
    }
  }

  async getAlarmCountByStatus(): Promise<Record<AlarmStatus, number>> {
    try {
      const endpoint = `${ENDPOINTS.ALARMS.BASE}/count-by-status`;
      const cacheKey = 'alarm_count_by_status';
      
      const response = await apiClient.customGet<Record<AlarmStatus, number>>(endpoint, {
        cacheKey,
        cacheTTL: 30 * 1000
      });
      
      return response.data || {};
    } catch (error) {
      console.error('Get alarm count by status error:', error);
      throw error;
    }
  }

  // ==================== ALARM HISTORY ====================

  async getAlarmHistory(alarmId: ID): Promise<Alarm[]> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.ALARMS.HISTORY, alarmId);
      const cacheKey = `alarm_history_${alarmId}`;
      
      const response = await apiClient.customGet<Alarm[]>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get alarm history ${alarmId} error:`, error);
      throw error;
    }
  }

  async getAlarmTimeline(alarmId: ID): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.ALARMS.BASE}/${alarmId}/timeline`;
      const cacheKey = `alarm_timeline_${alarmId}`;
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000
      });
      
      return response.data;
    } catch (error) {
      console.error(`Get alarm timeline ${alarmId} error:`, error);
      throw error;
    }
  }

  async getRelatedAlarms(alarmId: ID): Promise<Alarm[]> {
    try {
      const endpoint = createEndpoint(ENDPOINTS.ALARMS.RELATED, alarmId);
      const cacheKey = `related_alarms_${alarmId}`;
      
      const response = await apiClient.customGet<Alarm[]>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 1000
      });
      
      return response.data || [];
    } catch (error) {
      console.error(`Get related alarms ${alarmId} error:`, error);
      throw error;
    }
  }

  // ==================== EXPORT OPERATIONS ====================

  async exportAlarms(format: ExportFormat, params?: AlarmQueryParams): Promise<Blob> {
    try {
      const endpoint = ENDPOINTS.ALARMS.EXPORT;
      return await apiClient.exportData(endpoint, format, params);
    } catch (error) {
      console.error('Export alarms error:', error);
      throw error;
    }
  }

  async exportAlarmReport(alarmId: ID, format: ExportFormat): Promise<Blob> {
    try {
      const endpoint = `${ENDPOINTS.ALARMS.BASE}/${alarmId}/export-report`;
      return await apiClient.exportData(endpoint, format);
    } catch (error) {
      console.error(`Export alarm report ${alarmId} error:`, error);
      throw error;
    }
  }

  // ==================== FILTER & CONFIGURATION ====================

  async getAlarmFilters(): Promise<AlarmFilterOptions> {
    try {
      const endpoint = ENDPOINTS.ALARMS.FILTERS;
      const cacheKey = 'alarm_filters';
      
      const response = await apiClient.customGet<AlarmFilterOptions>(endpoint, {
        cacheKey,
        cacheTTL: 10 * 60 * 1000 // 10 dakika
      });
      
      return response.data;
    } catch (error) {
      console.error('Get alarm filters error:', error);
      throw error;
    }
  }

  async getAlarmTypes(): Promise<AlarmType[]> {
    try {
      const endpoint = `${ENDPOINTS.ALARMS.BASE}/types`;
      const cacheKey = 'alarm_types';
      
      const response = await apiClient.customGet<AlarmType[]>(endpoint, {
        cacheKey,
        cacheTTL: 30 * 60 * 1000 // 30 dakika
      });
      
      return response.data || [];
    } catch (error) {
      console.error('Get alarm types error:', error);
      throw error;
    }
  }

  async getAlarmConfig(): Promise<any> {
    try {
      const endpoint = `${ENDPOINTS.ALARMS.BASE}/config`;
      const cacheKey = 'alarm_config';
      
      const response = await apiClient.customGet<any>(endpoint, {
        cacheKey,
        cacheTTL: 60 * 60 * 1000 // 1 saat
      });
      
      return response.data;
    } catch (error) {
      console.error('Get alarm config error:', error);
      throw error;
    }
  }

  // ==================== REAL-TIME/WEBSOCKET ====================

  subscribeToAlarms(callback: (alarm: Alarm) => void): () => void {
    this.subscribers.push(callback);
    
    // İlk subscriber ise stream'i başlat
    if (this.subscribers.length === 1) {
      this.startAlarmStream();
    }
    
    // Unsubscribe function
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }
      
      // Son subscriber ise stream'i durdur
      if (this.subscribers.length === 0) {
        this.stopAlarmStream();
      }
    };
  }

  unsubscribeFromAlarms(): void {
    this.subscribers = [];
    this.stopAlarmStream();
  }

  getAlarmStream(params?: AlarmQueryParams): EventSource {
    const queryString = params ? `?${new URLSearchParams(params as any).toString()}` : '';
    const streamUrl = `${ENDPOINTS.ALARMS.BASE}/stream${queryString}`;
    
    return new EventSource(streamUrl);
  }

  // ==================== PRIVATE METHODS ====================

  private normalizeQueryParams(params?: AlarmQueryParams): any {
    if (!params) return {};
    
    const normalized: any = { ...params };
    
    // Array'leri string'e çevir
    if (Array.isArray(normalized.severity)) {
      normalized.severity = normalized.severity.join(',');
    }
    
    if (Array.isArray(normalized.status)) {
      normalized.status = normalized.status.join(',');
    }
    
    if (Array.isArray(normalized.unitId)) {
      normalized.unitId = normalized.unitId.join(',');
    }
    
    if (Array.isArray(normalized.wagonId)) {
      normalized.wagonId = normalized.wagonId.join(',');
    }
    
    // Date'leri ISO string'e çevir
    if (normalized.startDate instanceof Date) {
      normalized.startDate = normalized.startDate.toISOString();
    }
    
    if (normalized.endDate instanceof Date) {
      normalized.endDate = normalized.endDate.toISOString();
    }
    
    // Undefined'leri temizle
    for (const key of Object.keys(normalized)) {
      if (normalized[key] === undefined || normalized[key] === null) {
        delete normalized[key];
      }
    }
    
    return normalized;
  }

  private generateCacheKey(prefix: string, params?: any): string {
    if (!params) return prefix;
    
    const paramString = JSON.stringify(params);
    const hash = this.hashString(paramString);
    return `${prefix}_${hash}`;
  }

  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.codePointAt(i);
      if (char !== undefined) {
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
      }
    }
    return Math.abs(hash).toString(36);
  }

  private clearAlarmCache(id: ID): void {
    apiClient.cancelRequest(`alarm_${id}`);
  }

  private clearAlarmsCache(): void {
    // Alarm list cache'ini temizle
    apiClient.cancelRequest('alarms');
  }

  private startAlarmStream(): void {
    try {
      this.alarmStream = this.getAlarmStream();
      
      this.alarmStream.onmessage = (event) => {
        try {
          const alarm: Alarm = JSON.parse(event.data);
          this.notifySubscribers(alarm);
        } catch (error) {
          console.error('Failed to parse alarm stream message:', error);
        }
      };
      
      this.alarmStream.onerror = (error) => {
        console.error('Alarm stream error:', error);
        // Reconnect logic eklenebilir
      };
      
    } catch (error) {
      console.error('Failed to start alarm stream:', error);
    }
  }

  private stopAlarmStream(): void {
    if (this.alarmStream) {
      this.alarmStream.close();
      this.alarmStream = null;
    }
  }

  private notifySubscribers(alarm: Alarm): void {
    for (const callback of this.subscribers) {
      try {
        callback(alarm);
      } catch (error) {
        console.error('Alarm subscriber error:', error);
      }
    }
  }
}

// Singleton instance
export const alarmService = new AlarmService();

// Default export
export default alarmService;